import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Question } from 'types';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { questionGeneration, type QuestionGenerationState } from 'services/api/questionGenerationClient';

interface Params {
  question: Question | null;
  flowName: string;
  flowRunId: number | null;
  flowItemId?: number;
  enabled: boolean;
}

export const useGeneratedQuestion = ({ question, flowName, flowRunId, flowItemId, enabled }: Params) => {
  const required = enabled && hasQuestionGeneration(question);
  const itemId = flowItemId ?? question?.flow_item_id;
  const key = required ? `${flowName}:${flowRunId}:${itemId}:${question?.id}:${question?.updated_at ?? ''}` : null;
  const [result, setResult] = useState<{ key: string; state?: QuestionGenerationState; error?: string } | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => setRetryToken((value) => value + 1), []);

  useEffect(() => {
    if (!required || !key) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setResult({ key });
    if (!flowRunId || !itemId) {
      setResult({ key, error: 'Your question could not be opened. Reload this page to try again.' });
      return;
    }
    const load = async (poll: boolean) => {
      try {
        const state = await (poll ? questionGeneration.get : questionGeneration.ensure)(flowName, itemId, flowRunId);
        if (!active) return;
        if (state.status === 'ready' && state.generation_id && state.question_text && !state.restart_required) {
          setResult({ key, state });
        } else if (state.status === 'generating') {
          setResult({ key, state });
          timer = setTimeout(() => { void load(true); }, 1500);
        } else {
          setResult({ key, state, error: state.restart_required
            ? 'This question has changed. Start a new module attempt to answer the updated question.'
            : 'Your question could not be loaded. Please try again.' });
        }
      } catch (caught) {
        if (!active) return;
        const state = (caught as { response?: { data?: { state?: QuestionGenerationState } } }).response?.data?.state;
        setResult({ key, state, error: state?.restart_required
          ? 'This question has changed. Start a new module attempt to answer the updated question.'
          : 'Your question could not be loaded. Please try again.' });
      }
    };
    void load(false);
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [required, key, flowName, flowRunId, itemId, retryToken]);

  const current = result?.key === key ? result : null;
  const ready = !required || (current?.state?.status === 'ready' && !current.error && !current.state.restart_required);
  const displayedQuestion = useMemo(() => required && ready && question && current?.state?.question_text
    ? { ...question, text: current.state.question_text } : question, [current?.state?.question_text, question, ready, required]);
  return {
    question: displayedQuestion,
    generationId: required && ready ? current?.state?.generation_id ?? undefined : undefined,
    loading: required && !ready && !current?.error,
    error: required ? current?.error : undefined,
    retry,
    ready: Boolean(ready),
    restartRequired: required && Boolean(current?.state?.restart_required),
  };
};
